import { skipToken } from '@apollo/client/react'
import { useNavigate } from 'react-router'
import { match, P } from 'ts-pattern'
import { withQuery } from 'ufo'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import FluentLinkMultiple20Filled from '~icons/fluent/link-multiple-20-filled'
import IcBaselineKeyboardArrowDown from '~icons/ic/baseline-keyboard-arrow-down'
import IcBaselineKeyboardArrowUp from '~icons/ic/baseline-keyboard-arrow-up'
import ArtworkStatistics from 'src/components/ArtworkDetail/ArtworkStatistics'
import MediaPreview from 'src/components/Common/MediaPreview'
import { useBaseCursorPagingContext } from 'src/components/CursorPaging/BaseCursorPaging'
import { CancelAndRefundTaskButton } from 'src/components/GenerationTask/CancelAndRefundTaskButton'
import { prefillImageToVideoPanel } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/context'
import { PublishTaskButton } from 'src/components/GenerationTask/PublishTaskButton'
import {
  CancelWithoutRefundButton,
  DeleteTaskButton,
  RerunTaskButton,
} from 'src/components/GenerationTask/TaskDetail/TaskActionButton'
import TaskPlaceholderImage from 'src/components/GenerationTask/TaskPlaceholderImage'
import { dialogInspectionForRAC } from 'src/components/Modal/InspectDialog'
import { getArtworkShareLink } from 'src/components/SocialShare/SocialShare'
import { TaskDetailParameters } from 'src/components/TaskList/TaskDetailParameters'
import { TaskTypeLabel } from 'src/components/TaskList/TaskPreviewContentExtra'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogFooter,
  UIDialogHeader,
  type UIModalProps,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useCopy } from 'src/hooks/useCopy'
import {
  TaskCancelability,
  type Task,
  type TaskSummary,
} from 'src/services/apollo/generated/graphql'
import { useTaskById } from 'src/services/apollo/task'
import { getNaturalPrompts } from 'src/services/helpers/artworkParamsExtractor'
import { useMe } from 'src/services/helpers/user'
import { getArtworkTitle } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import {
  getMediaUrl,
  getPublicUrl,
  getVideoMediaIdFromTask,
} from 'src/utils/media'
import {
  getMediaIdsFromTask,
  isI2vProTask,
  isReferenceVideoTask,
  isTaskAllowedToPublishAsArtwork,
  isVideoTask,
  TaskStatus,
} from 'src/utils/task'
import { startEventGroup, track } from 'src/utils/tracker'
import { ExpandableContentBox } from '../Common/ExpandableContentBox'
import { ChatWorkspaceCreatorButton } from '../ConversationEdit/Creator/ChatWorkspaceCreatorButton'
import { MembershipIconCircled } from '../Membership/MembershipIcon'
import styles from './TaskDetailDialog.module.css'

type TaskDetailDialogProps = Pick<UIModalProps, 'open' | 'onClose'> & {
  task?: string | Task
  taskSummary?: TaskSummary
}

export default function TaskDetailDialog({
  task: taskInput,
  open,
  onClose,
  taskSummary,
}: TaskDetailDialogProps) {
  const {
    data: task = typeof taskInput === 'string' ? undefined : taskInput,
    refetch: refetchTask,
  } = useTaskById(
    typeof taskInput === 'string' ? taskInput : (taskInput?.id ?? skipToken),
  )
  const { media, parameters = {} } = task ?? {}
  const mediaIds = task ? getMediaIdsFromTask(task) : []
  const videoMediaId = getVideoMediaIdFromTask(task)
  const prompts = isI2vProTask(task)
    ? parameters?.i2vPro?.prompts
    : isReferenceVideoTask(task)
      ? parameters?.referenceVideo?.prompt
      : parameters.prompts
  /**
   * I2V tasks (i2vPro / referenceVideo) live in a dedicated panel at
   * `/generator/image/animation` and carry their prompt under
   * `parameters.i2vPro.prompts` or `parameters.referenceVideo.prompt`. The
   * default text-to-image importer reads `parameters.prompts` and lacks the
   * i2v-specific controls (model / duration / camera), so importing an i2v
   * task there silently drops every i2v parameter. Route the click to the
   * animation tab and seed the i2v jotai atoms via `prefillImageToVideoPanel`
   * before navigating — same pattern as `ArtworkActionButton`'s
   * "use as reference" admin flow.
   */
  const isI2vTask = isI2vProTask(task) || isReferenceVideoTask(task)
  const goToTaskUrl =
    task?.status !== 'completed'
      ? '#'
      : isI2vTask
        ? withQuery('/generator/image/animation', {
            // Only i2vPro single-image input maps to URL params; refVideo's
            // multi-reference media ids are restored from the form-stage atom
            // by `prefillImageToVideoPanel`, so leave the query empty.
            mediaId: parameters?.i2vPro?.mediaId,
            tailMediaId: parameters?.i2vPro?.tailMediaId,
          })
        : // taskId instead of encoded task blob — avoids 431 from oversized URL
          withQuery('/generator/image', {
            taskId: task?.id,
            showOnStage: '1',
          })
  const promptHelperEnabled =
    parameters?.promptHelper?.enable || parameters?.promptHelper?.withStage
  const [copy, copied] = useCopy()
  const { beingAdmin } = useMe()
  const { mutate: reloadPage } = useBaseCursorPagingContext()

  const preferDrawer = useBreakpoint('msm')

  const displayTaskId =
    task?.id ?? (typeof taskInput === 'string' ? taskInput : taskInput?.id)

  const navigate = useNavigate()

  return (
    <UIDialog
      open={open}
      onClose={onClose}
      type={preferDrawer ? 'drawer' : 'dialog'}
      dismissable
      className={cx(
        styles['dialog-content-layout'],
        'overflow-x-hidden overflow-y-auto',
        'msm:w-[calc(100vw-4rem)] msm:min-w-[600px] msm:max-w-screen-mlg max-h-[calc(100vh-4rem)] max-mmd:flex flex-col',
      )}
    >
      <UIDialogHeader heading="" className="hidden" />
      <section
        className={cx(
          'p-4 overflow-auto max-mmd:snap-x snap-mandatory bg-zinc-100 dark:bg-black/20 max-mmd:border-b mmd:border-r border-black/10 dark:border-white/10',
          'w-full max-mmd:h-[clamp(300px,100vw,400px)]',
          'flex mmd:flex-col items-center gap-4 *:flex-none max-mmd:first:*:ml-auto max-mmd:last:*:mr-auto mmd:first:*:mt-auto mmd:last:*:mb-auto',
        )}
        style={{ gridArea: 'media' }}
      >
        {task && mediaIds.length > 0 ? (
          <>
            {mediaIds.map(mediaId => (
              <MediaPreview
                key={mediaId}
                mediaId={mediaId}
                videoMediaId={videoMediaId}
                thumbnail
                className="max-w-[calc(100%-2rem)] mmd:max-w-full max-h-full mmd:max-h-[calc(100%-2rem)] snap-center snap-always rounded ring-1 ring-black/10 dark:ring-white/10"
                onClick={() => {
                  window.open(
                    getMediaUrl(videoMediaId ?? mediaId, 'orig'),
                    '_blank',
                  )
                }}
              />
            ))}
          </>
        ) : (
          <TaskPlaceholderImage
            id={task?.id}
            status={task?.status || TaskStatus.waiting}
            className="size-full aspect-square rounded"
          />
        )}
      </section>
      <div
        className="flex flex-col gap-4 p-4 overflow-auto"
        style={{ gridArea: 'info' }}
      >
        {/* Type label */}
        <section className="flex items-start has-[[data-no-content]:first-child]:hidden">
          <TaskTypeLabel taskSummary={taskSummary} />
          {displayTaskId && (
            <div className="text-xs text-neutral-500 font-mono ml-2 py-1 select-text">
              {displayTaskId}
            </div>
          )}
          {beingAdmin && (
            <UIButton
              variant="outlined"
              color="admin"
              className="ml-auto flex-none"
              onPress={() => {
                dialogInspectionForRAC(task)
              }}
            >
              Inspect
            </UIButton>
          )}
          <div data-no-content="" />
        </section>
        {/* Status info */}
        <section className="has-[[data-no-content]]:hidden">
          {match(task)
            .with({ status: 'completed' }, task =>
              task.artworks?.length ? (
                <>
                  <header className="mb-2 flex items-end gap-4">
                    <h3 className="flex-1 text-lg">
                      <Text
                        i18nKey="artwork:type.artwork"
                        values={{ count: task.artworks.length }}
                      />
                    </h3>
                  </header>
                  <div className="flex max-msm:flex-col msm:flex-wrap gap-2">
                    {task.artworks.map(artwork => (
                      <div
                        key={artwork.id}
                        className="flex items-center gap-4 text-sm"
                      >
                        <MediaPreview
                          mediaId={artwork.mediaId}
                          thumbnail
                          className="flex-none w-20 rounded [&_img]:object-contain"
                        />
                        <div className="flex-1 flex flex-col gap-2">
                          <p>{getArtworkTitle(artwork)}</p>
                          <div className="flex items-center gap-2">
                            <ArtworkStatistics artworkId={artwork.id} />
                            <UIButton
                              variant="ghost"
                              icon={
                                copied ? (
                                  <FluentCheckmark16Filled className="size-5 text-green-500 mr-1" />
                                ) : (
                                  <FluentLinkMultiple20Filled className="size-5 text-icon mr-1" />
                                )
                              }
                              className="px-1 capitalize"
                              onPress={() => {
                                copy(getArtworkShareLink(artwork.id))
                              }}
                            >
                              <Text
                                i18nKey={
                                  copied ? 'action:copied' : 'action:copy.link'
                                }
                              />
                            </UIButton>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (task?.parameters.batchSize ?? 0) <= 1 &&
                isTaskAllowedToPublishAsArtwork(task) ? ( // only non-batch tasks can be published here
                <div className="flex flex-col gap-2 w-fit">
                  <PublishTaskButton task={task} scene="profile" />
                  {/* Chat editing is image-only; hide it for video tasks (i2vPro / referenceVideo / animateDiff / t2i2v) where it would otherwise seed a workspace from the still start-frame. */}
                  {!isVideoTask(task) && (
                    <ChatWorkspaceCreatorButton
                      trackingArgs={{
                        location: 'task-pop-up',
                        modelId: task?.parameters?.modelId,
                      }}
                      variant="outlined"
                      color="theme"
                      className="rounded-full px-6 h-9 text-sm font-semibold"
                      icon={<MembershipIconCircled className="w-5 h-5" />}
                      initialImageId={media?.id}
                    >
                      <Text i18nKey="artwork:start-chat-editing.label" />
                    </ChatWorkspaceCreatorButton>
                  )}
                </div>
              ) : (
                <div data-no-content="" />
              ),
            )
            .with(
              {
                status: 'waiting',
                cancelability: P.not(TaskCancelability.Cancelable),
              },
              task => <CancelWithoutRefundButton task={task} />,
            )
            .with({ status: 'failed' }, task => (
              <RerunTaskButton task={task} onSuccess={() => refetchTask()} />
            ))
            .otherwise(() => (
              <div data-no-content="" />
            ))}
        </section>
        {/* Natural Prompts */}
        <section className="has-[[data-no-content]]:hidden">
          {promptHelperEnabled ? (
            <div>
              <header className="mb-2 flex items-end gap-4">
                <h3 className="flex-1 text-lg">
                  <Text i18nKey="generation:original-prompts.label" />
                </h3>
              </header>
              <p className="text-sm">{getNaturalPrompts(parameters)}</p>
            </div>
          ) : (
            <div data-no-content="" />
          )}
        </section>
        {/* Prompt */}
        <section>
          <header className="mb-2 flex items-end gap-4">
            <h3 className="flex-1 text-lg">
              <Text i18nKey="artwork:prompts.prompts" />
            </h3>
          </header>
          {promptHelperEnabled ? (
            // Tasks with prompt helper
            <div>
              <ExpandableContentBox
                className="max-h-40 data-[expanded]:max-h-fit"
                toggle={({ expanded, setExpanded }) => (
                  <button
                    className="flex items-center gap-1 text-xs opacity-60 hover:opacity-80 transition-opacity mt-1"
                    onClick={() => setExpanded(val => !val)}
                  >
                    {expanded ? (
                      <>
                        <IcBaselineKeyboardArrowUp className="size-3" />
                        <Text i18nKey="artwork:show.less" />
                      </>
                    ) : (
                      <>
                        <IcBaselineKeyboardArrowDown className="size-3" />
                        <Text i18nKey="artwork:show.more" />
                      </>
                    )}
                  </button>
                )}
              >
                <p className="text-sm">{prompts}</p>
              </ExpandableContentBox>
            </div>
          ) : (
            // Tasks without prompt helper
            <div>
              <p className="text-sm">{prompts}</p>
            </div>
          )}
        </section>
        {/* Parameters */}
        {task && (
          <section className="flex flex-col">
            <TaskDetailParameters task={task} />
          </section>
        )}
      </div>
      <UIDialogFooter
        className="sticky bottom-0 max-msm:flex-col text-sm mmd:rounded-bl-none"
        style={{ gridArea: 'footer' }}
      >
        {task && (
          <>
            {match(task)
              .with({ cancelability: TaskCancelability.Cancelable }, task => (
                <CancelAndRefundTaskButton
                  taskId={task.id}
                  className="max-msm:order-last"
                  onSuccess={() => {
                    onClose?.()
                    refetchTask()
                  }}
                />
              ))
              .otherwise(task => (
                <DeleteTaskButton
                  task={task}
                  variant="filled"
                  color="danger"
                  className="max-msm:order-last"
                  onSuccess={() => {
                    onClose?.()
                    reloadPage()
                  }}
                >
                  <Text i18nKey="artwork:delete-task.label" />
                </DeleteTaskButton>
              ))}
            <span className="mr-auto max-msm:hidden" />
          </>
        )}
        {task?.status === 'completed' && (
          <UIButton
            variant="filled"
            onPress={() => {
              track('go_to_task_click', { taskId: task.id })
              startEventGroup('generate_source')
              if (isI2vTask) {
                prefillImageToVideoPanel({ task })
              }
              navigate(goToTaskUrl)
            }}
          >
            <Text i18nKey="user:task.go-to-task" context="preview" />
          </UIButton>
        )}
      </UIDialogFooter>
    </UIDialog>
  )
}
